import { styled } from '@portfolio/styles'
import { CloseV12 } from '@portfolio/icons'
import { useHorizontalNavigation } from '@portfolio/utils'
import { Link } from '../Link/Link'

const ScrollContainer = styled('div', {
  position: 'relative',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  width: '100vw',
  variants: {
    isVisible: {
      true: {
        opacity: 1,
      },
    },
  },
  '@md': {
    width: 'fit-content',
    margin: '0 auto',
    opacity: 1,
  },
})

const Tags = styled('div', {
  display: 'grid',
  gridAutoFlow: 'column',
  overflowY: 'hidden',
  overflowX: 'auto',
  width: '100%',
  scrollSnapType: 'x mandatory',
  gap: '$12',
  padding: '0 $20 $72',

  '&::-webkit-scrollbar': {
    display: 'none',
  },

  '@md': {
    width: 'fit-content',
    alignItems: 'center',
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingTop: '$4',
  },
})

const Tag = styled(Link, {
  projectFont: 'ui02',
  background: 'none',
  color: '$black',
  borderRadius: '$rMax',
  border: '1px solid $black',
  display: 'inline-flex',
  gap: '$8',
  padding: '$8 $16',
  textDecoration: 'none',
  textAlign: 'center',
  alignItems: 'center',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
  transition: '0.2s color, 0.2s background',
  '@lg': {
    '&:hover': {
      color: '$white',
      background: '$black',
    },
  },
  '&:active': {
    color: '$white',
    background: '$black_08',
  },
  '&:focus-visible': {
    outline: '$focusBlue solid 3px;',
    color: '$white',
    background: '$black',
  },

  variants: {
    selected: {
      true: {
        color: '$white',
        background: '$black',
      },
      false: {
        background: 'none',
        color: '$black',
      },
    },
  },
})

type TagsGroupProps = {
  tags: {
    title: string
    value: string
    selected: boolean
    href: string
  }[]
  additionalTag?: {
    title: string
    href: string
  }
}

export const TagsGroup = ({ tags, additionalTag }: TagsGroupProps) => {
  const selectedTagValue = tags.find((tag) => tag.selected)?.value || null

  const { linkListRef, isVisible, setItemRef } = useHorizontalNavigation({
    activeId: selectedTagValue,
  })

  return (
    <ScrollContainer isVisible={isVisible}>
      <Tags ref={linkListRef}>
        {additionalTag && (
          <Tag selected href={additionalTag.href}>
            <CloseV12 fill={'white'} />
            {additionalTag.title}
          </Tag>
        )}
        {tags.map((tag) => (
          <Tag
            ref={(el) => setItemRef(el, tag.value)}
            selected={tag.selected}
            key={tag.value}
            href={tag.href}
          >
            {tag.title}
          </Tag>
        ))}
      </Tags>
    </ScrollContainer>
  )
}
